/**
 * TMDB API — Movie/Series poster fetching for the Highlights page.
 *
 * TMDB poster widths available: 92 | 154 | 185 | 342 | 500 | 780 | original
 *
 * Poster size selection strategy:
 *   Cards are 283px wide in the 1280×720 CSS coordinate space.
 *   The app-shell is scaled by Math.min(vw/1280, vh/720), and the browser
 *   further multiplies by devicePixelRatio.
 *   We pick the smallest TMDB size whose pixel width is >= the physical
 *   rendered width so posters are never blurry.
 */

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p';

/** Cloudflare Worker proxy — TMDB API key lives only in the Worker secret. */
const TMDB_WORKER_URL = (process.env.REACT_APP_TMDB_WORKER_URL || '').replace(/\/$/, '');

/** TMDB poster sizes in ascending pixel width order */
const POSTER_SIZES = [92, 154, 185, 342, 500, 780] as const;

/** TMDB backdrop sizes in ascending pixel width order */
const BACKDROP_SIZES = [300, 780, 1280] as const;

/** Card width in the base 1280×720 CSS coordinate space (px) */
const CSS_CARD_WIDTH = 283;

/**
 * Pick the smallest TMDB poster width that covers the physically rendered
 * card width on the current device/viewport.
 */
function pickPosterSize(): string {
  const scaleX = window.innerWidth / 1280;
  const scaleY = window.innerHeight / 720;
  const scale = Math.min(scaleX, scaleY);
  const dpr = window.devicePixelRatio || 1;
  const physicalCardWidth = Math.ceil(CSS_CARD_WIDTH * scale * dpr);

  const fit = POSTER_SIZES.find((s) => s >= physicalCardWidth);
  return fit ? `w${fit}` : 'original';
}

/**
 * Pick the smallest TMDB backdrop width that covers the physically rendered
 * split-card slot. The split slot is 283px wide × 241px tall in CSS space.
 * To cover it with a 16:9 backdrop, the required width ≈ 241 × 1.78 ≈ 429px.
 */
function pickBackdropSize(): string {
  const scaleX = window.innerWidth / 1280;
  const scaleY = window.innerHeight / 720;
  const scale = Math.min(scaleX, scaleY);
  const dpr = window.devicePixelRatio || 1;
  // height drives the constraint: split card height = 241px CSS → upscale to fill
  const CSS_SPLIT_HEIGHT = 241;
  const physicalNeededWidth = Math.ceil(CSS_SPLIT_HEIGHT * 1.78 * scale * dpr);

  const fit = BACKDROP_SIZES.find((s) => s >= physicalNeededWidth);
  return fit ? `w${fit}` : 'w1280';
}

export interface TmdbMovie {
  id: number;
  title: string;
  original_title: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date: string;
  vote_average: number;
  popularity: number;
  genre_ids: number[];
}

export interface TmdbMovieResult {
  id: number;
  title: string;
  overview: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  releaseDate: string;
  voteAverage: number;
}

function mapMovie(movie: TmdbMovie, posterSize: string): TmdbMovieResult {
  return {
    id: movie.id,
    title: movie.title,
    overview: movie.overview,
    posterUrl: movie.poster_path
      ? `${TMDB_IMAGE_BASE}/${posterSize}${movie.poster_path}`
      : null,
    backdropUrl: null, // overridden by caller with correct size
    releaseDate: movie.release_date,
    voteAverage: movie.vote_average,
  };
}

/** Auto-detection maps (mirrors LanguageSection.tsx) */
const LANG_TO_TMDB_LANG: Record<string, string> = {
  'pt-BR': 'pt-BR', 'en': 'en-US', 'es': 'es-ES',
  'fr': 'fr-FR', 'de': 'de-DE', 'ar': 'ar', 'it': 'it-IT', 'tr': 'tr-TR',
};
const LANG_TO_REGION: Record<string, string> = {
  'pt-BR': 'BR', 'en': 'US', 'es': 'ES',
  'fr': 'FR', 'de': 'DE', 'ar': 'SA', 'it': 'IT', 'tr': 'TR',
};

/**
 * Detect the best TMDB language from the browser's navigator.languages.
 * Falls back to 'en-US' if nothing matches.
 */
function detectBrowserTmdbLang(): string {
  const langs: readonly string[] = navigator.languages?.length
    ? navigator.languages
    : [navigator.language ?? 'en'];
  for (const l of langs) {
    const base = l.split('-')[0];
    if (l === 'pt-BR' || base === 'pt') return 'pt-BR';
    if (base === 'es') return 'es-ES';
    if (base === 'fr') return 'fr-FR';
    if (base === 'de') return 'de-DE';
    if (base === 'ar') return 'ar';
    if (base === 'it') return 'it-IT';
    if (base === 'tr') return 'tr-TR';
    if (base === 'en') return 'en-US';
  }
  return 'en-US';
}

/**
 * Detect the best TMDB region from the browser's navigator.languages.
 * Falls back to 'US' if nothing matches.
 */
function detectBrowserRegion(): string {
  const langs: readonly string[] = navigator.languages?.length
    ? navigator.languages
    : [navigator.language ?? 'en'];
  for (const l of langs) {
    const parts = l.split('-');
    // e.g. 'pt-BR' → 'BR', 'en-US' → 'US'
    if (parts.length >= 2) return parts[parts.length - 1].toUpperCase();
  }
  return 'US';
}

/** Effective TMDB language.
 * Priority: explicit 'ott_tmdb_lang' > app lang 'ott_lang' > browser detection > 'en-US'
 */
export const TMDB_LANGUAGE = (() => {
  const explicit = localStorage.getItem('ott_tmdb_lang');
  if (explicit) return explicit;
  const appLang = localStorage.getItem('ott_lang');
  if (appLang && LANG_TO_TMDB_LANG[appLang]) return LANG_TO_TMDB_LANG[appLang];
  return detectBrowserTmdbLang();
})();

export function getRuntimeTmdbLanguage(appLanguage?: string): string {
  const explicit = localStorage.getItem('ott_tmdb_lang');
  if (explicit) return explicit;
  const normalized = appLanguage || localStorage.getItem('ott_lang') || '';
  if (normalized && LANG_TO_TMDB_LANG[normalized]) return LANG_TO_TMDB_LANG[normalized];
  if (normalized && /^[a-z]{2}(?:-[A-Z]{2})?$/.test(normalized)) return normalized;
  return TMDB_LANGUAGE;
}

/** Effective TMDB region.
 * Priority: explicit 'ott_tmdb_region' > app lang 'ott_lang' > browser detection > 'US'
 */
export const TMDB_REGION = (() => {
  const explicit = localStorage.getItem('ott_tmdb_region');
  if (explicit) return explicit;
  const appLang = localStorage.getItem('ott_lang');
  if (appLang && LANG_TO_REGION[appLang]) return LANG_TO_REGION[appLang];
  return detectBrowserRegion();
})();

export function getRuntimeTmdbRegion(appLanguage?: string): string {
  const explicit = localStorage.getItem('ott_tmdb_region');
  if (explicit) return explicit;
  const normalized = appLanguage || localStorage.getItem('ott_lang') || '';
  if (normalized && LANG_TO_REGION[normalized]) return LANG_TO_REGION[normalized];
  return TMDB_REGION;
}

/** Build a query string from a plain object — no URL/URLSearchParams API needed (Chrome 47 compat). */
function buildQs(params: Record<string, string>): string {
  return Object.keys(params)
    .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]); })
    .join('&');
}

async function apiFetch<T>(path: string, params: Record<string, string> = {}): Promise<T> {
  if (!TMDB_WORKER_URL) {
    throw new Error('TMDB worker URL is not configured');
  }

  const qs = buildQs(Object.assign({ path: path }, params));
  const url = TMDB_WORKER_URL + '?' + qs;
  const res = await fetch(url);
  if (!res.ok) throw new Error('TMDB worker ' + res.status + ': ' + res.statusText);
  return res.json() as Promise<T>;
}

export interface TmdbPopularResponse {
  page: number;
  results: TmdbMovie[];
  total_pages: number;
  total_results: number;
}

/**
 * Fetch current popular movies from TMDB.
 * Returns up to `limit` mapped results with poster URLs sized for the
 * current viewport.
 */
export async function fetchPopularMovies(
  limit = 5,
  language = TMDB_LANGUAGE,
): Promise<TmdbMovieResult[]> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<TmdbPopularResponse>('/movie/popular', {
    language,
    page: '1',
  });
  return data.results.slice(0, limit).map((m) => ({
    ...mapMovie(m, posterSize),
    backdropUrl: m.backdrop_path
      ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
      : null,
  }));
}

/**
 * Same as fetchPopularMovies but returns both the display-ready results
 * AND the raw TmdbMovie objects (needed for VOD matching).
 */
export async function fetchPopularMoviesWithRaw(
  limit = 5,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbMovieResult[]; raw: TmdbMovie[] }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<TmdbPopularResponse>('/movie/popular', {
    language,
    page: '1',
  });
  const sliced = data.results.slice(0, limit);
  return {
    raw: sliced,
    results: sliced.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
        : null,
    })),
  };
}

/**
 * Fetch a specific page of popular movies (for paginated VOD matching).
 * Returns raw TmdbMovie[] + mapped TmdbMovieResult[].
 */
export async function fetchPopularMoviesPage(
  page: number,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbMovieResult[]; raw: TmdbMovie[]; totalPages: number }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<TmdbPopularResponse>('/movie/popular', {
    language,
    page: String(page),
  });
  return {
    raw: data.results,
    totalPages: data.total_pages,
    results: data.results.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
        : null,
    })),
  };
}

/**
 * Fetch current popular TV series from TMDB.
 */
export interface TmdbGenreItem {
  id: number;
  name: string;
}

export interface TmdbSeries {
  id: number;
  name: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  first_air_date: string;
  vote_average: number;
  genre_ids: number[];
}

export interface TmdbSeriesResult {
  id: number;
  title: string;
  overview: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  firstAirDate: string;
  voteAverage: number;
}

export async function fetchPopularSeries(
  limit = 5,
  language = TMDB_LANGUAGE,
): Promise<TmdbSeriesResult[]> {
  const posterSize = pickPosterSize();
  const data = await apiFetch<{ results: TmdbSeries[] }>('/tv/popular', {
    language,
    page: '1',
  });
  return data.results.slice(0, limit).map((s) => ({
    id: s.id,
    title: s.name,
    overview: s.overview,
    posterUrl: s.poster_path
      ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}`
      : null,
    backdropUrl: s.backdrop_path
      ? `${TMDB_IMAGE_BASE}/w1280${s.backdrop_path}`
      : null,
    firstAirDate: s.first_air_date,
    voteAverage: s.vote_average,
  }));
}

/**
 * Fetch a specific page of popular TV series (for paginated series matching).
 * Returns raw TmdbSeries[] + mapped TmdbSeriesResult[].
 */
export async function fetchPopularSeriesPage(
  page: number,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbSeriesResult[]; raw: TmdbSeries[]; totalPages: number }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/tv/popular', {
    language,
    page: String(page),
  });
  return {
    raw: data.results,
    totalPages: data.total_pages,
    results: data.results.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path
        ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}`
        : null,
      backdropUrl: s.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}`
        : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
}

// ─── Movie Details ────────────────────────────────────────────────────────────

export interface TmdbGenre {
  id: number;
  name: string;
}

export interface TmdbProductionCountry {
  iso_3166_1: string;
  name: string;
}

export interface TmdbCrewMember {
  job: string;
  name: string;
}

export interface TmdbCastMember {
  name: string;
  order: number;
}

export interface TmdbMovieDetails {
  id: number;
  title: string;
  original_title: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date: string;
  vote_average: number;
  genres: TmdbGenre[];
  production_countries: TmdbProductionCountry[];
  runtime: number | null;
  /** Appended via append_to_response=credits */
  credits?: {
    crew: TmdbCrewMember[];
    cast: TmdbCastMember[];
  };
  /** Appended via append_to_response=videos */
  videos?: {
    results: { key: string; site: string; type: string }[];
  };
}

export interface TmdbMovieDetailsResult {
  id: number;
  title: string;
  originalTitle: string;
  overview: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  releaseDate: string;
  voteAverage: number;
  genres: string[];
  countries: string[];
  director: string;
  cast: string[];
  trailerKey?: string;
}

// ─── Long-lived TMDB API cache ──────────────────────────────────────────────

const DETAILS_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const RECOMMENDATIONS_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const _apiCache = new Map<string, { data: unknown; expiresAt: number }>();
const _apiInflight = new Map<string, Promise<unknown>>();

function _storageKey(key: string): string {
  return 'ott_tmdb_api_v1_' + key.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function _getCached<T>(key: string): T | undefined {
  const entry = _apiCache.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) { _apiCache.delete(key); return undefined; }
  return entry.data as T;
}

function _setCached<T>(key: string, data: T, ttlMs = DETAILS_CACHE_TTL_MS): void {
  _apiCache.set(key, { data, expiresAt: Date.now() + ttlMs });
}

function _getPersistentCached<T>(key: string): T | undefined {
  const memory = _getCached<T>(key);
  if (memory !== undefined) return memory;
  try {
    const raw = localStorage.getItem(_storageKey(key));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { data: T; expiresAt: number };
    if (!parsed.expiresAt || Date.now() > parsed.expiresAt) {
      localStorage.removeItem(_storageKey(key));
      return undefined;
    }
    _apiCache.set(key, { data: parsed.data, expiresAt: parsed.expiresAt });
    return parsed.data;
  } catch {
    return undefined;
  }
}

function _setPersistentCached<T>(key: string, data: T, ttlMs: number): void {
  const expiresAt = Date.now() + ttlMs;
  _apiCache.set(key, { data, expiresAt });
  try {
    localStorage.setItem(_storageKey(key), JSON.stringify({ data, expiresAt }));
  } catch {
    // Storage quota is non-fatal; memory cache still helps this session.
  }
}

async function cachedApiFetch<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const cached = _getPersistentCached<T>(key);
  if (cached !== undefined) return cached;
  const inflight = _apiInflight.get(key) as Promise<T> | undefined;
  if (inflight) return inflight;
  const promise = fetcher().then((data) => {
    _setPersistentCached(key, data, ttlMs);
    return data;
  }).finally(() => {
    _apiInflight.delete(key);
  });
  _apiInflight.set(key, promise);
  return promise;
}

export async function fetchMovieDetails(
  movieId: number,
  language = TMDB_LANGUAGE,
): Promise<TmdbMovieDetailsResult> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();

  const data = await cachedApiFetch<TmdbMovieDetails>(
    `movie:details:${movieId}:${language}`,
    DETAILS_CACHE_TTL_MS,
    () => apiFetch<TmdbMovieDetails>(`/movie/${movieId}`, {
      language,
      append_to_response: 'credits,videos',
    }),
  );

  const director =
    data.credits?.crew.find((c) => c.job === 'Director')?.name ?? '';
  const cast =
    data.credits?.cast
      .sort((a, b) => a.order - b.order)
      .slice(0, 5)
      .map((c) => c.name) ?? [];

  const trailerKey =
    data.videos?.results.find((v) => v.site === 'YouTube' && v.type === 'Trailer')?.key;

  const result: TmdbMovieDetailsResult = {
    id: data.id,
    title: data.title,
    originalTitle: data.original_title,
    overview: data.overview,
    posterUrl: data.poster_path
      ? `${TMDB_IMAGE_BASE}/${posterSize}${data.poster_path}`
      : null,
    backdropUrl: data.backdrop_path
      ? `${TMDB_IMAGE_BASE}/${backdropSize}${data.backdrop_path}`
      : null,
    releaseDate: data.release_date,
    voteAverage: data.vote_average,
    genres: data.genres.map((g) => g.name),
    countries: data.production_countries.map((c) => c.name),
    director,
    cast,
    trailerKey,
  };
  return result;
}

export async function fetchSimilarMovies(
  movieId: number,
  limit = 8,
  language = TMDB_LANGUAGE,
): Promise<TmdbMovieResult[]> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await cachedApiFetch<TmdbPopularResponse>(
    `movie:recommendations:${movieId}:${language}`,
    RECOMMENDATIONS_CACHE_TTL_MS,
    () => apiFetch<TmdbPopularResponse>(`/movie/${movieId}/recommendations`, {
      language,
      page: '1',
    }),
  );
  return data.results.slice(0, limit).map((m) => ({
    ...mapMovie(m, posterSize),
    backdropUrl: m.backdrop_path
      ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
      : null,
  }));
}

/**
 * Same as fetchSimilarMovies but returns both the display-ready results
 * AND the raw TmdbMovie objects (needed for VOD matching).
 */
export async function fetchSimilarMoviesWithRaw(
  movieId: number,
  limit = 20,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbMovieResult[]; raw: TmdbMovie[] }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await cachedApiFetch<TmdbPopularResponse>(
    `movie:recommendations:${movieId}:${language}`,
    RECOMMENDATIONS_CACHE_TTL_MS,
    () => apiFetch<TmdbPopularResponse>(`/movie/${movieId}/recommendations`, {
      language,
      page: '1',
    }),
  );
  const sliced = data.results.slice(0, limit);
  return {
    raw: sliced,
    results: sliced.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
        : null,
    })),
  };
}

// ─── TV / Series details ──────────────────────────────────────────────────────

interface TmdbTvDetails {
  id: number;
  name: string;
  original_name: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  first_air_date: string;
  vote_average: number;
  genres: TmdbGenre[];
  production_countries: TmdbProductionCountry[];
  created_by: { id: number; name: string }[];
  number_of_seasons: number;
  credits?: {
    cast: TmdbCastMember[];
    crew: TmdbCrewMember[];
  };
  videos?: {
    results: { key: string; site: string; type: string }[];
  };
}

export interface TmdbTvDetailsResult {
  id: number;
  title: string;
  originalTitle: string;
  overview: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  firstAirDate: string;
  voteAverage: number;
  genres: string[];
  countries: string[];
  creators: string[];
  cast: string[];
  numberOfSeasons: number;
  trailerKey?: string;
}

export async function fetchTvDetails(
  seriesId: number,
  language = TMDB_LANGUAGE,
): Promise<TmdbTvDetailsResult> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await cachedApiFetch<TmdbTvDetails>(
    `tv:details:${seriesId}:${language}`,
    DETAILS_CACHE_TTL_MS,
    () => apiFetch<TmdbTvDetails>(`/tv/${seriesId}`, {
      language,
      append_to_response: 'credits,videos',
    }),
  );
  const creators = (data.created_by ?? []).map((c) => c.name);
  const cast =
    data.credits?.cast
      .sort((a, b) => a.order - b.order)
      .slice(0, 5)
      .map((c) => c.name) ?? [];
  const trailerKey =
    data.videos?.results.find((v) => v.site === 'YouTube' && v.type === 'Trailer')?.key;

  const result: TmdbTvDetailsResult = {
    id: data.id,
    title: data.name,
    originalTitle: data.original_name,
    overview: data.overview,
    posterUrl: data.poster_path
      ? `${TMDB_IMAGE_BASE}/${posterSize}${data.poster_path}`
      : null,
    backdropUrl: data.backdrop_path
      ? `${TMDB_IMAGE_BASE}/${backdropSize}${data.backdrop_path}`
      : null,
    firstAirDate: data.first_air_date,
    voteAverage: data.vote_average,
    genres: data.genres.map((g) => g.name),
    countries: (data.production_countries ?? []).map((c) => c.name),
    creators,
    cast,
    numberOfSeasons: data.number_of_seasons ?? 0,
    trailerKey,
  };
  return result;
}

/**
 * Fetch TMDB /tv/{seriesId}/recommendations, returning both display results
 * and raw TmdbSeries objects (needed for Xtream matching).
 */
export async function fetchSimilarSeriesWithRaw(
  seriesId: number,
  limit = 20,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbSeriesResult[]; raw: TmdbSeries[] }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await cachedApiFetch<{ results: TmdbSeries[]; total_results: number; total_pages: number; page: number }>(
    `tv:recommendations:${seriesId}:${language}`,
    RECOMMENDATIONS_CACHE_TTL_MS,
    () => apiFetch<{ results: TmdbSeries[]; total_results: number; total_pages: number; page: number }>(
      `/tv/${seriesId}/recommendations`,
      { language, page: '1' },
    ),
  );
  const sliced = data.results.slice(0, limit);
  return {
    raw: sliced,
    results: sliced.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}` : null,
      backdropUrl: s.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}` : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
}

// ─── Movie search by title ────────────────────────────────────────────────────

/**
 * Strips Xtream-style suffixes from a stream name before sending to TMDB search.
 * e.g. "Breaking Bad [DUB] S01 (2008) 4K" → "Breaking Bad"
 */
export function sanitizeTitleForSearch(name: string): string {
  return name
    .replace(/\[.*?\]/g, '')           // [DUB], [LEG], [4K], [PT-BR]
    .replace(/\(.*?\)/g, '')           // (DUB), (2008), (Legendado), (x265)
    .replace(/\b(19|20)\d{2}\b/g, '')  // standalone year: 2008, 1999
    .replace(/\bS\d{1,2}(E\d{1,2})?\b/gi, '') // S01, S1, S01E02
    .replace(/\b(4K|HD|FHD|UHD|SDR|HDR|HEVC|AVC|x265|x264)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface TmdbIdentityCandidate {
  id: number;
  title: string;
  originalTitle: string;
  overview: string;
  date: string;
}

/** Lightweight search results used by the trusted content identity resolver. */
export async function searchMovieIdentityCandidates(
  title: string,
  language = TMDB_LANGUAGE,
): Promise<TmdbIdentityCandidate[]> {
  const sanitized = sanitizeTitleForSearch(title);
  if (!sanitized) return [];
  const data = await apiFetch<{ results: TmdbMovie[] }>('/search/movie', {
    query: sanitized,
    language,
    page: '1',
  });
  return data.results.slice(0, 5).map((item) => ({
    id: item.id,
    title: item.title,
    originalTitle: item.original_title,
    overview: item.overview || '',
    date: item.release_date || '',
  }));
}

/** Lightweight TV search results used by the trusted content identity resolver. */
export async function searchSeriesIdentityCandidates(
  title: string,
  language = TMDB_LANGUAGE,
): Promise<TmdbIdentityCandidate[]> {
  const sanitized = sanitizeTitleForSearch(title);
  if (!sanitized) return [];
  const data = await apiFetch<{ results: Array<TmdbSeries & { original_name?: string }> }>('/search/tv', {
    query: sanitized,
    language,
    page: '1',
  });
  return data.results.slice(0, 5).map((item) => ({
    id: item.id,
    title: item.name,
    originalTitle: item.original_name || item.name,
    overview: item.overview || '',
    date: item.first_air_date || '',
  }));
}

// ─── Trailer key fetchers ─────────────────────────────────────────────────────

export async function fetchMovieTrailerKey(
  movieId: number,
  language = TMDB_LANGUAGE,
): Promise<string | undefined> {
  const cacheKey = `movie:trailer:${movieId}:${language}`;
  const cached = _getCached<string>(cacheKey);
  if (cached) return cached;
  const data = await apiFetch<{ results: { key: string; site: string; type: string }[] }>(
    `/movie/${movieId}/videos`,
    { language },
  );
  const key = data.results.find((v) => v.site === 'YouTube' && v.type === 'Trailer')?.key;
  if (key) _setCached(cacheKey, key);
  return key;
}

export async function fetchTvTrailerKey(
  seriesId: number,
  language = TMDB_LANGUAGE,
): Promise<string | undefined> {
  const cacheKey = `tv:trailer:${seriesId}:${language}`;
  const cached = _getCached<string>(cacheKey);
  if (cached) return cached;
  const data = await apiFetch<{ results: { key: string; site: string; type: string }[] }>(
    `/tv/${seriesId}/videos`,
    { language },
  );
  const key = data.results.find((v) => v.site === 'YouTube' && v.type === 'Trailer')?.key;
  if (key) _setCached(cacheKey, key);
  return key;
}

// ─── TV Season episode data ───────────────────────────────────────────────────

/** Single episode as returned by TMDB /tv/{id}/season/{n} */
interface TmdbSeasonEpisodeRaw {
  episode_number: number;
  name: string;
  overview: string;
  runtime: number | null;
  air_date: string | null;
}

/** Mapped episode data from TMDB — used to enrich Xtream episodes */
export interface TmdbSeasonEpisode {
  episodeNumber: number;
  title: string;
  overview: string;
  /** Runtime in minutes (or null if unknown) */
  runtime: number | null;
  airDate: string | null;
}

/**
 * Fetch all episodes of a single season from TMDB.
 * Returns a map keyed by episode_number for O(1) lookup.
 * Cached 1 h in memory (same cache as other TMDB detail calls).
 */
export async function fetchTvSeason(
  seriesId: number,
  seasonNumber: number,
  language = TMDB_LANGUAGE,
): Promise<Map<number, TmdbSeasonEpisode>> {
  const cacheKey = `tv:season:${seriesId}:${seasonNumber}:${language}`;
  const cached = _getCached<Map<number, TmdbSeasonEpisode>>(cacheKey);
  if (cached) return cached;

  try {
    const data = await apiFetch<{ episodes: TmdbSeasonEpisodeRaw[] }>(
      `/tv/${seriesId}/season/${seasonNumber}`,
      { language },
    );
    const map = new Map<number, TmdbSeasonEpisode>();
    for (const ep of data.episodes ?? []) {
      map.set(ep.episode_number, {
        episodeNumber: ep.episode_number,
        title: ep.name,
        overview: ep.overview,
        runtime: ep.runtime ?? null,
        airDate: ep.air_date ?? null,
      });
    }
    _setCached(cacheKey, map);
    return map;
  } catch {
    // Return empty map on error — Xtream data used as fallback
    return new Map();
  }
}

// ─── Now Playing / On The Air (cached 24 h) ──────────────────────────────────

const TMDB_CACHE_TTL = 24 * 60 * 60 * 1000;

function loadTmdbCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: T; ts: number };
    if (Date.now() - ts > TMDB_CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function saveTmdbCache<T>(key: string, data: T): void {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Fetch all pages of TMDB /movie/now_playing (up to 5 pages).
 * Cached 24 h in localStorage.
 */
export async function fetchNowPlayingMovies(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }> {
  const CACHE_KEY = `ott_now_playing_v1_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbMovie[] = [];
  const first = await apiFetch<TmdbPopularResponse>('/movie/now_playing', { language, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 5);
  for (let p = 2; p <= maxPages; p++) {
    const page = await apiFetch<TmdbPopularResponse>('/movie/now_playing', { language, page: String(p) });
    allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}` : null,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch movies released in the last 12 months via TMDB /discover/movie.
 * Sorted by release_date descending. Up to 5 pages. Cached 24 h in localStorage.
 * Cache key includes the date so results stay fresh after each daily reload.
 */
export async function fetchRecentMovies(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }> {
  const today = new Date();
  const yearAgo = new Date(today);
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  // Cache key includes today's date so it refreshes daily
  const CACHE_KEY = `ott_recent_movies_v1_${TMDB_LANGUAGE}_${toISO(today)}`;
  const cached = loadTmdbCache<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbMovie[] = [];
  const commonParams: Record<string, string> = {
    language,
    sort_by: 'popularity.desc',
    'primary_release_date.gte': toISO(yearAgo),
    'primary_release_date.lte': toISO(today),
    ...(TMDB_REGION ? { region: TMDB_REGION } : {}),
  };
  const first = await apiFetch<TmdbPopularResponse>('/discover/movie', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50); // up to 1000 results (20 per page)
  // Fetch remaining pages in parallel batches of 10 to stay fast without hammering the API
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<TmdbPopularResponse>('/discover/movie', { ...commonParams, page: String(batchStart + i) })
      )
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}` : null,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch the list of official TV genres from TMDB.
 * Cached 24 h in localStorage.
 */
export async function fetchTvGenres(
  language = TMDB_LANGUAGE,
): Promise<TmdbGenreItem[]> {
  const CACHE_KEY = `ott_tv_genres_v1_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<TmdbGenreItem[]>(CACHE_KEY);
  if (cached) return cached;
  const data = await apiFetch<{ genres: TmdbGenreItem[] }>('/genre/tv/list', { language });
  saveTmdbCache(CACHE_KEY, data.genres);
  return data.genres;
}

/**
 * Fetch TV series from the user's country via TMDB /discover/tv.
 * Uses with_origin_country from the effective TMDB region.
 * Up to 50 pages (1000 results). Fetched in parallel batches of 10.
 * Cached 24 h in localStorage.
 */
export async function fetchNationalSeries(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }> {
  const CACHE_KEY = `ott_national_series_v2_${TMDB_LANGUAGE}_${TMDB_REGION}`;
  const cached = loadTmdbCache<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbSeries[] = [];
  const commonParams: Record<string, string> = {
    language,
    sort_by: 'popularity.desc',
    with_origin_country: TMDB_REGION,
    watch_region: TMDB_REGION,
  };
  const first = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50);
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', { ...commonParams, page: String(batchStart + i) })
      )
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}` : null,
      backdropUrl: s.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}` : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch the top-rated movies of all time via TMDB /movie/top_rated.
 * Fetched in parallel batches of 10 pages (up to 500 pages max).
 * Cached 24 h in localStorage.
 */
export async function fetchTopMoviesAll(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }> {
  const CACHE_KEY = `ott_top_movies_v2_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbMovie[] = [];
  const commonParams: Record<string, string> = {
    language,
    ...(TMDB_REGION ? { region: TMDB_REGION } : {}),
  };
  const first = await apiFetch<TmdbPopularResponse>('/movie/top_rated', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50);
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<TmdbPopularResponse>('/movie/top_rated', { ...commonParams, page: String(batchStart + i) }),
      ),
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
        : null,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch the top-rated TV series of all time via TMDB /tv/top_rated.
 * Fetched in parallel batches of 10 pages (up to 500 pages max).
 * Cached 24 h in localStorage.
 */
export async function fetchTopSeriesAll(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }> {
  const CACHE_KEY = `ott_top_series_v3_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbSeries[] = [];
  const commonParams: Record<string, string> = {
    language,
    sort_by: 'vote_average.desc',
    without_genres: '16',
    'vote_count.gte': '200',
    ...(TMDB_REGION ? { region: TMDB_REGION } : {}),
  };
  const first = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50);
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', { ...commonParams, page: String(batchStart + i) }),
      ),
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}` : null,
      backdropUrl: s.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}` : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch all pages of TMDB /tv/on_the_air (up to 5 pages).
 * Cached 24 h in localStorage.
 */
export async function fetchOnAirSeries(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }> {
  const CACHE_KEY = `ott_on_air_v1_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbSeries[] = [];
  const first = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/tv/on_the_air', { language, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 5);
  for (let p = 2; p <= maxPages; p++) {
    const page = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/tv/on_the_air', { language, page: String(p) });
    allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}` : null,
      backdropUrl: s.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}` : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch animated / family movies for the Kids section via TMDB /discover/movie.
 * Filters: with_genres=16|10751 (Animation OR Family), sorted by popularity.
 * Up to 10 pages (200 results). Cached 24 h in localStorage.
 */
export async function fetchKidsMovies(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }> {
  const CACHE_KEY = `ott_kids_movies_v2_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbMovie[] = [];
  const commonParams: Record<string, string> = {
    language,
    sort_by: 'popularity.desc',
    with_genres: '16|10751',
    'vote_count.gte': '50',
    include_adult: 'false',
  };
  const first = await apiFetch<TmdbPopularResponse>('/discover/movie', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50);
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<TmdbPopularResponse>('/discover/movie', { ...commonParams, page: String(batchStart + i) })
      )
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}` : null,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch Kids TV series for the Kids section via TMDB /discover/tv.
 * Filters: with_genres=10762 (Kids genre), sorted by popularity.
 * Up to 10 pages (200 results). Cached 24 h in localStorage.
 */
export async function fetchKidsSeries(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }> {
  const CACHE_KEY = `ott_kids_series_v2_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbSeries[] = [];
  const commonParams: Record<string, string> = {
    language,
    sort_by: 'popularity.desc',
    with_genres: '10762',
  };
  const first = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50);
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', { ...commonParams, page: String(batchStart + i) })
      )
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const result = {
    raw: allRaw,
    results: allRaw.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}` : null,
      backdropUrl: s.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}` : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
  saveTmdbCache(CACHE_KEY, result);
  return result;
}

/**
 * Fetch Anime TV series for the Anime section.
 * Merges two TMDB queries (keyword 210024 = anime + genre 16 without Kids),
 * deduplicating by TMDB id. Keyword results come first (more accurate).
 * Cached 24 h in localStorage.
 */
export async function fetchAnimeSeries(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }> {
  const CACHE_KEY = `ott_anime_series_v2_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbSeries[]; results: TmdbSeriesResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();

  const fetchPages = async (params: Record<string, string>): Promise<TmdbSeries[]> => {
    const first = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', {
      ...params, page: '1',
    });
    const raw: TmdbSeries[] = [...first.results];
    const maxPages = Math.min(first.total_pages, 50);
    const BATCH = 10;
    for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
      const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
      const pages = await Promise.all(
        Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
          apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', {
            ...params, page: String(batchStart + i),
          })
        )
      );
      for (const page of pages) raw.push(...page.results);
    }
    return raw;
  };

  const baseParams = { language, sort_by: 'popularity.desc' };
  const [animeTagRaw, animationRaw] = await Promise.all([
    fetchPages({ ...baseParams, with_keywords: '210024' }),
    fetchPages({ ...baseParams, with_genres: '16', without_genres: '10762' }),
  ]);

  // Keyword results first, then animation results not already present
  const seen = new Set<number>();
  const allRaw: TmdbSeries[] = [];
  for (const s of [...animeTagRaw, ...animationRaw]) {
    if (!seen.has(s.id)) { seen.add(s.id); allRaw.push(s); }
  }

  const animeSeriesResult = {
    raw: allRaw,
    results: allRaw.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}` : null,
      backdropUrl: s.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}` : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
  saveTmdbCache(CACHE_KEY, animeSeriesResult);
  return animeSeriesResult;
}

/**
 * Fetch animated movies for the Anime section via TMDB /discover/movie.
 * Filters: genre 16 (Animation) without genre 10751 (Family), no adult content.
 * Sorted by popularity. Up to 10 pages. Cached 24 h in localStorage.
 */
export async function fetchAnimeMovies(
  language = TMDB_LANGUAGE,
): Promise<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }> {
  const CACHE_KEY = `ott_anime_movies_v2_${TMDB_LANGUAGE}`;
  const cached = loadTmdbCache<{ raw: TmdbMovie[]; results: TmdbMovieResult[] }>(CACHE_KEY);
  if (cached) return cached;

  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const allRaw: TmdbMovie[] = [];
  const commonParams: Record<string, string> = {
    language,
    sort_by: 'popularity.desc',
    with_genres: '16',
    without_genres: '10751',
    include_adult: 'false',
    'vote_count.gte': '50',
  };
  const first = await apiFetch<TmdbPopularResponse>('/discover/movie', { ...commonParams, page: '1' });
  allRaw.push(...first.results);
  const maxPages = Math.min(first.total_pages, 50);
  const BATCH = 10;
  for (let batchStart = 2; batchStart <= maxPages; batchStart += BATCH) {
    const batchEnd = Math.min(batchStart + BATCH - 1, maxPages);
    const pages = await Promise.all(
      Array.from({ length: batchEnd - batchStart + 1 }, (_, i) =>
        apiFetch<TmdbPopularResponse>('/discover/movie', { ...commonParams, page: String(batchStart + i) })
      )
    );
    for (const page of pages) allRaw.push(...page.results);
  }
  const animeMoviesResult = {
    raw: allRaw,
    results: allRaw.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}` : null,
    })),
  };
  saveTmdbCache(CACHE_KEY, animeMoviesResult);
  return animeMoviesResult;
}

// ─── Paged single-page fetch variants (stop-early incremental matching) ───────

export async function fetchKidsMoviesPage(
  page: number,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbMovieResult[]; raw: TmdbMovie[]; totalPages: number }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<TmdbPopularResponse>('/discover/movie', {
    language,
    page: String(page),
    sort_by: 'popularity.desc',
    with_genres: '16|10751',
    'vote_count.gte': '50',
    include_adult: 'false',
  });
  return {
    raw: data.results,
    totalPages: data.total_pages,
    results: data.results.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
        : null,
    })),
  };
}

export async function fetchKidsSeriesPage(
  page: number,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbSeriesResult[]; raw: TmdbSeries[]; totalPages: number }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', {
    language,
    page: String(page),
    sort_by: 'popularity.desc',
    with_genres: '10762',
  });
  return {
    raw: data.results,
    totalPages: data.total_pages,
    results: data.results.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path
        ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}`
        : null,
      backdropUrl: s.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}`
        : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
}

export async function fetchAnimeSeriesPage(
  page: number,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbSeriesResult[]; raw: TmdbSeries[]; totalPages: number }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<{ results: TmdbSeries[]; total_pages: number }>('/discover/tv', {
    language,
    page: String(page),
    sort_by: 'popularity.desc',
    with_keywords: '210024',
  });
  return {
    raw: data.results,
    totalPages: data.total_pages,
    results: data.results.map((s) => ({
      id: s.id,
      title: s.name,
      overview: s.overview,
      posterUrl: s.poster_path
        ? `${TMDB_IMAGE_BASE}/${posterSize}${s.poster_path}`
        : null,
      backdropUrl: s.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${s.backdrop_path}`
        : null,
      firstAirDate: s.first_air_date,
      voteAverage: s.vote_average,
    })),
  };
}

export async function fetchAnimeMoviesPage(
  page: number,
  language = TMDB_LANGUAGE,
): Promise<{ results: TmdbMovieResult[]; raw: TmdbMovie[]; totalPages: number }> {
  const posterSize = pickPosterSize();
  const backdropSize = pickBackdropSize();
  const data = await apiFetch<TmdbPopularResponse>('/discover/movie', {
    language,
    page: String(page),
    sort_by: 'popularity.desc',
    with_genres: '16',
    without_genres: '10751',
    include_adult: 'false',
    'vote_count.gte': '50',
  });
  return {
    raw: data.results,
    totalPages: data.total_pages,
    results: data.results.map((m) => ({
      ...mapMovie(m, posterSize),
      backdropUrl: m.backdrop_path
        ? `${TMDB_IMAGE_BASE}/${backdropSize}${m.backdrop_path}`
        : null,
    })),
  };
}
